import React, { useEffect, useState } from 'react';
import { useMode } from '../contexts/ModeContext';
import { useNavigate } from 'react-router-dom';
import { getTranslation } from '../translations';
import { progressAPI } from '../utils/api';
import { ArrowLeft, TrendingUp, Award, Target } from 'lucide-react';
import { toast } from 'sonner';

const Statistics = () => {
  const { isKidsMode, language } = useMode();
  const navigate = useNavigate();
  const t = (key) => getTranslation(language, key);
  
  const [stats, setStats] = useState(null);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, progressRes] = await Promise.all([
        progressAPI.getStats(),
        progressAPI.getUserProgress()
      ]);
      setStats(statsRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      toast.error('İstatistikler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{
        background: isKidsMode
          ? 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)'
          : 'linear-gradient(135deg, #F5F5F4 0%, #E7E5E4 100%)'
      }}
    >
      {/* Header */}
      <header className="px-6 py-4 bg-white/50 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1
            className={`text-2xl font-bold ${
              isKidsMode ? 'font-kids text-green-500' : 'font-elegant text-emerald-900'
            }`}
          >
            {t('statistics')}
          </h1>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Overview */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div
            className={`p-6 ${
              isKidsMode
                ? 'bg-white rounded-3xl shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
                : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200'
            }`}
            data-testid="total-attempts-card"
          >
            <TrendingUp className={`w-10 h-10 mb-4 ${isKidsMode ? 'text-green-400' : 'text-emerald-700'}`} />
            <div className="text-3xl font-bold mb-1">{stats?.total_attempts || 0}</div>
            <div className="text-sm text-gray-600">{t('total_attempts')}</div>
          </div>

          <div
            className={`p-6 ${
              isKidsMode
                ? 'bg-white rounded-3xl shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
                : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200'
            }`}
            data-testid="success-rate-card"
          >
            <Award className={`w-10 h-10 mb-4 ${isKidsMode ? 'text-yellow-400' : 'text-amber-600'}`} />
            <div className="text-3xl font-bold mb-1">{Math.round(stats?.success_rate || 0)}%</div>
            <div className="text-sm text-gray-600">{t('success_rate')}</div>
          </div>

          <div
            className={`p-6 ${
              isKidsMode
                ? 'bg-white rounded-3xl shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
                : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200'
            }`}
            data-testid="surahs-practiced-card"
          >
            <Target className={`w-10 h-10 mb-4 ${isKidsMode ? 'text-pink-400' : 'text-teal-600'}`} />
            <div className="text-3xl font-bold mb-1">{stats?.surahs_practiced || 0}</div>
            <div className="text-sm text-gray-600">Sure Çalışıldı</div>
          </div>
        </div>

        {/* Recent Practice */}
        <div
          className={`p-6 ${
            isKidsMode
              ? 'bg-white rounded-3xl shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
              : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200'
          }`}
        >
          <h2 className={`text-2xl font-bold mb-6 ${isKidsMode ? 'font-kids' : 'font-elegant'}`}>
            {t('recent_practice')}
          </h2>
          
          {progress.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Henüz çalışma kaydı yok</p>
          ) : (
            <div className="space-y-4" data-testid="progress-list">
              {progress.slice(0, 10).map((item, idx) => (
                <div
                  key={idx}
                  className={`p-4 border rounded-lg flex justify-between items-center ${
                    isKidsMode ? 'border-slate-200 rounded-2xl' : 'border-stone-200'
                  }`}
                  data-testid={`progress-item-${idx}`}
                >
                  <div>
                    <div className="font-medium">
                      Sure {item.surah_number}, Ayet {item.ayah_from}-{item.ayah_to}
                    </div>
                    <div className="text-sm text-gray-500">
                      Başarı: {item.successful_attempts}/{item.total_attempts} - Hata: {item.total_errors}
                    </div>
                  </div>
                  <div className="text-sm text-gray-400">
                    {new Date(item.last_practiced).toLocaleDateString('tr-TR')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Statistics;
